<template>
  <div class="alarms-widget widget overflow-x-hidden position-relative" :class="{'user-alarms-height': isUserAlarms}">
    <div class="accordion" role="tablist">

      <div
        class="widget-header mx-auto cursor-pointer"
        v-show="isToolbarsCollapsed"
        @click.prevent="updateToolbarsStatus"
      >
        <feather-icon
          class="text-body mx-1 text-white icon-hover-grow"
          icon="BellIcon"
          stroke-width="2"
          size="17"
          v-b-tooltip.hover.right.viewport="`Alarms`"
        />
      </div>

      <div class="widget-header" v-show="!isToolbarsCollapsed">

        <div
          class="icon-title"
          v-b-toggle="collapsible ? `main-accordion-${id}` : ''"
          v-if="!searchBarEnabled"
        >
          <feather-icon
            class="text-body mx-1 text-white"
            icon="BellIcon"
            stroke-width="2"
            size="17"
            :style="{ stroke: '#fff' }"
          />
          <p class="text-center">
            {{ title }}
            <template v-if="meta.total"> ({{ meta.total }})</template>
          </p>

        </div>

        <div class="search-collapse" :class="[{ barEnable: searchBarEnabled }]">
          <div class="d-flex align-items-center">
            <b-icon v-if="infiniteScrollEnabled" icon="chevron-expand"></b-icon>
            <b-icon v-else icon="chevron-bar-down"></b-icon>
            <b-form-checkbox
              v-model="infiniteScrollEnabled"
              switch
              size="sm"
              class="checkbox-alarms"
            >
            </b-form-checkbox>

            <div v-if="isFetchingMore" class="table-loading-overlay">
              <b-icon icon="disc" animation="spin-pulse"></b-icon>
            </div>
          </div>

          <img
            :src="require('@/assets/svg/search.svg')"
            alt="Search"
            class="mx-1"
            width="18"
            v-b-tooltip.hover.bottom.viewport="`${$t('search')}`"
            @click="toggleSearchBar"
            v-if="!searchBarEnabled"
          />

          <search-bar-widgets
            :search-bar-width="searchBarWidth"
            :sidebar-min-width-reached="sidebarMinWidthReached"
            @input="onTypeSearch"
            @toggle-search-bar="toggleSearchBar"
            @reset-search-bar="resetSearchBar"
            v-else
          />

          <template v-if="collapsible">
            <feather-icon
              v-if="!isConfigModeOn && !searchBarEnabled"
              :icon="isCollapseOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              v-b-toggle="collapsible ? `main-accordion-${id}` : ''"
              size="24"
            />
          </template>

          <span
            class="handle cursor-move"
            v-if="isConfigModeOn"
            :style="{ opacity: !searchBarEnabled ? 1 : 0 }"
          >
            <feather-icon icon="MenuIcon" size="18"/>
          </span>

        </div>
      </div>
    </div>

    <b-collapse
      :id="`main-accordion-${id}`"
      :accordion="`main-accordion-${id}`"
      role="tabpanel"
      visible
      v-model="isCollapseOpen"
      v-show="isCollapseOpen && !isToolbarsCollapsed"
      @input="onUpdateStatus"
    >
      <!-- ============== Data table ============== -->
      <div
        ref="scrollRoot"
        class="alarms-scroll-container"
        :class="{ 'infinite-active': infiniteScrollEnabled }"
        :style="scrollContainerStyle"
        @scroll="onUserScroll"
      >

      <table-component
        :items="items"
        :location="location"
        :filters="filters"
        :isUserAlarms="isUserAlarms"
        :busy="isBusy"
        @fetch-data="fetchData"
        @row-metrics="onRowMetrics"
        :idTable="id"
        @sort-changed="onUpdateSortBy"
      />

      <div
        v-if="infiniteScrollEnabled && pagination.next"
        ref="scrollSentinel"
        class="scroll-sentinel"
      ></div>

      </div>
      <!-- ======================================== -->

      <div class="d-flex justify-content-end align-content-center mt-0 mb-1">
        <div class="d-flex align-items-center pr-1 border-right-light">
          <span>{{ $t("grid.widgets.alarms.pagination.showing") }}</span>
          <select
            v-model.number="defaultFilters.max"
            class="mx-50"
            @change="onChangeRecordesPerPage"
          >
            <option v-for="rpp in recordsPerPage" :key="rpp" :value="rpp">
              {{ rpp }}
            </option>
          </select>
          <span>{{ $t("grid.widgets.alarms.pagination.records") }}</span>
        </div>

        <div
          class="pagination-count d-flex align-items-center mr-25"
          v-if="this.sidebarWidth > 570"
        >
          {{ $t("grid.widgets.alarms.pagination.showing") }}
          {{ items.length }} {{ $t("grid.widgets.alarms.pagination.of") }}
          {{ meta.total }} ({{ $t("grid.widgets.alarms.pagination.page") }}
          {{ meta.current_page }}
          {{ $t("grid.widgets.alarms.pagination.of") }} {{ meta.last_page }})
        </div>
        <div class="pagination d-flex justify-content-end align-items-center" v-if="!infiniteScrollEnabled">
          <a
            href="#"
            :class="[
              'btn btn-sm btn-secondary px-50 rounded-0',
              { disabled: !pagination.prev },
            ]"
            @click="clickPaginationBtn('prev')"
          >
            <feather-icon icon="ChevronLeftIcon" size="16"/>
          </a>
          <a
            href="#"
            :class="[
              'btn btn-sm btn-secondary px-50 rounded-0',
              { disabled: !pagination.next },
            ]"
            @click="clickPaginationBtn('next')"
          >
            <feather-icon icon="ChevronRightIcon" size="16"/>
          </a>
        </div>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import {BCollapse, VBToggle} from "bootstrap-vue";
import store from "@/store";
import {debounce} from "lodash";
import TableComponent from "./TableComponent.vue";
import axios from "axios";
import SearchBarWidgets from "../components/SearchBarWidgets.vue";
import { EventBus } from "@/libs/event-bus";
import { alarmIdentity, applyAlarmRecurrence, applyAlarmDeviceState, preserveAlarmDeviceState } from "@/utils/alarmRecurrence";

const DEFAULT_RECORDS_PER_PAGE = 5;

function getWaitingAlarmsRecordsPerPage() {
  const recordsPerPage = Number(
    window.__env?.VUE_APP_WAITING_ALARMS_RECORDS_PER_PAGE
  );

  return Number.isInteger(recordsPerPage) && recordsPerPage > 0
    ? recordsPerPage
    : DEFAULT_RECORDS_PER_PAGE;
}

function isWaitingAlarmsInfiniteScrollEnabled() {
  const value = window.__env?.VUE_APP_WAITING_ALARMS_INFINITE_SCROLL;

  return value === true || value === "true" || value === 1 || value === "1";
}

export default {
  components: {
    BCollapse,
    TableComponent,
    SearchBarWidgets,
  },
  directives: {
    "b-toggle": VBToggle,
  },
  props: {
    id: {
      required: true,
      type: Number | String,
    },
    location: {
      type: String,
      default: "sideBar",
    },
    parentCollapsed: {
      type: Boolean,
      default: false,
    },
    filters: {
      type: Object,
    },
    title: {
      type: String | Number,
    },
    collapsible: {
      type: Boolean,
      default: true,
    },
    isOpen: {
      type: Boolean,
      default: true,
    },
    ignoreCollapse: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const isWaitingAlarms = this.filters?.filter === "waiting";
    const recordsPerPage = isWaitingAlarms
      ? getWaitingAlarmsRecordsPerPage()
      : DEFAULT_RECORDS_PER_PAGE;

    return {
      items: [],
      recordsPerPage: [...new Set([5, 10, 25, 50, 100, recordsPerPage])],
      defaultFilters: {
        max: recordsPerPage,
        sortBy: this.sortBy,
      },
      extraFilters: {
        q: null,
      },
      pagination: {
        first: null,
        last: null,
        next: null,
        prev: null,
      },
      meta: {},
      isBusy: false,
      dataModal: {
        id: this.title,
        data: {},
      },
      isCollapseOpen: false,
      searchQuery: "",
      searchBarEnabled: false,
      searchBarWidth: 0,
      source: axios.CancelToken.source(),
      infiniteScrollEnabled:
        isWaitingAlarms && isWaitingAlarmsInfiniteScrollEnabled(),
      isFetchingMore: false,
      observer: null,
      hasUserScrolled: false,
      measuredRowHeight: null,
      measuredHeaderHeight: null,
      sortBy: 'created',
      sortDesc: true,
    };
  },
  created() {
    store.dispatch("grid/loadVisited");
    this.refreshDataDebounced = debounce(() => this.fetchData(), 250);
  },
  mounted() {
    this.fetchData().then(() => {
      if (this.infiniteScrollEnabled) {
        this.$nextTick(() => this.initIntersectionObserver());
      }
    });
    this.isCollapseOpen = this.isOpen;
    this.searchBarWidth = this.$el.offsetWidth - 20;

    EventBus.on("update-alarms", this.onAlarmsUpdated);
    EventBus.on("alarm-recurrence-updated", this.onAlarmRecurrenceUpdated);
    EventBus.on("alarm-device-state-updated", this.onAlarmDeviceStateUpdated);
    document.addEventListener("visibilitychange", this.onVisibilityChange);

  },
  computed: {
    isConfigModeOn() {
      return store.state.psimConfig.configModeStatus;
    },
    sidebarMinWidthReached() {
      return store.getters["grid/sidebarMinWidthReached"];
    },
    sidebarWidth() {
      if (this.location === "sideBarRigth") {
        return store.state.grid.sidebarRightWidth;
      }

      return store.state.grid.sidebarWidth;
    },
    isToolbarsCollapsed() {
      if (this.ignoreCollapse) return false;
      return store.state.psimConfig.isToolbarsCollapsed;
    },
    isUserAlarms() {
      return this.filters.filter === 'user_alarms';
    },
    isReviewAlarms() {
      return this.filters.filter === 'in_review';
    },
    isMaintenanceAlarms() {
      return this.filters.filter === 'in_maintenance';
    },
    scrollContainerStyle() {
      if (!this.infiniteScrollEnabled) return {};
      if (!this.measuredRowHeight || !this.measuredHeaderHeight) return {};

      const maxRows = Number(this.defaultFilters.max);
      const visibleRows = Math.min(this.items.length, maxRows);

      return {
        height: `${
          visibleRows * this.measuredRowHeight +
          this.measuredHeaderHeight
        }px`,
        overflowY: "auto",
      };
    },
    takenAlarms() {
      const _takenAlarms = store.getters["taken_alarms/getTakenAlarms"];
      return _takenAlarms ? _takenAlarms.map(tA => tA.id_alarm) : [];
    }
  },
  watch: {
    "$store.state.grid.alarmsTS"() {
      this.fetchData();
    },
    sidebarWidth() {
      this.searchBarWidth = this.$el.offsetWidth - 20;
    },
    parentCollapsed() {
      if (this.location === "sideBarRigth") {
        this.searchBarWidth = this.$el.offsetWidth - 20;
      }
    },
    "$store.state.taken_alarms.takenAlarms"() {
      this.fetchData();
    },
    infiniteScrollEnabled(enabled) {
      this.$nextTick(() => {
        if (enabled) {
          this.initIntersectionObserver();
        } else {
          if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
          }
          this.items = [];
          this.fetchData();
        }
      });
    },
  },
  methods: {
    onAlarmDeviceStateUpdated(payload) {
      this.items.forEach(alarm => applyAlarmDeviceState(alarm, payload, this.$set));
    },
    onAlarmRecurrenceUpdated(payload) {
      this.items.forEach(alarm => applyAlarmRecurrence(alarm, payload, this.$set));
    },
    onAlarmsUpdated(alarm) {
      this.applyAlarmUpdate(alarm);
      this.refreshDataDebounced();
    },
    onVisibilityChange() {
      if (document.visibilityState === "visible") {
        this.refreshDataDebounced();
      }
    },
    applyAlarmUpdate(alarm) {
      if (!alarm?.id) return;

      const index = this.items.findIndex(item => alarmIdentity(item) === alarmIdentity(alarm));
      if (index === -1) return;

      const currentAlarm = this.items[index];
      const recurrenceCount = Math.max(
        Number(currentAlarm.recurrence_count) || 0,
        Number(alarm.recurrence_count) || 0
      );

      const updatedAlarm = { ...currentAlarm, ...alarm, recurrence_count: recurrenceCount };
      preserveAlarmDeviceState(updatedAlarm, currentAlarm, this.$set);
      this.$set(this.items, index, updatedAlarm);
    },
    preserveRecurrenceCounts(alarms) {
      const currentAlarms = new Map(this.items.map(alarm => [alarmIdentity(alarm), alarm]));
      return alarms.map(alarm => {
        const currentAlarm = currentAlarms.get(alarmIdentity(alarm));
        // A list request started before the recurrence must preserve its device
        // state as well as the counter. Equal counts can contain a later restore.
        if (Number(currentAlarm?.recurrence_count) > (Number(alarm.recurrence_count) || 0)) {
          applyAlarmRecurrence(alarm, currentAlarm, this.$set);
        }
        preserveAlarmDeviceState(alarm, currentAlarm, this.$set);
        return alarm;
      });
    },
    onUpdateSortBy(value) {
      this.sortBy = value.sortBy;
      this.sortDesc = value.sortDesc;
      this.fetchData();
    },
    onUpdateStatus(value) {
      this.$emit("widget-collapsed", {
        id: this.id,
        property: "isOpen",
        location: this.location,
        value,
      });
    },
    async fetchData(url, { append = false } = {}) {
      this.source.cancel();
      this.source = axios.CancelToken.source();
      this.isBusy = !append;

      const userAlarmsUrl = '/v1/alarms/workflow/user-alarms';
      const inReviewAlarmsUrl = '/v1/alarms/workflow/in-review';
      const inMaintenanceAlarmsUrl = '/v1/alarms/workflow/in-maintenance';
      let alarmsUrl  = '';

      if (this.isUserAlarms) {
        alarmsUrl = `${window.__env.VUE_APP_BASE_URL}${userAlarmsUrl}`;
      } else if (this.isReviewAlarms) {
        alarmsUrl = `${window.__env.VUE_APP_BASE_URL}${inReviewAlarmsUrl}`;
      } else if (this.isMaintenanceAlarms) {
        alarmsUrl = `${window.__env.VUE_APP_BASE_URL}${inMaintenanceAlarmsUrl}`;
      } else {
        alarmsUrl = window.__env.VUE_APP_MS_ALARMS_URL;
      }

      const params = url
        ? {}
        : {
            ...this.defaultFilters,
            ...this.filters,
            ...this.extraFilters,
            sortBy: this.sortBy,
            sortDesc: this.sortDesc,
          };

      try {
        const result = await axios.get(url || alarmsUrl, {
          params,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "X-Host": window.__env.VUE_APP_BASE_URL,
          },
          cancelToken: this.source.token,
        });

        const alarms = this.preserveRecurrenceCounts(result.data.data);

        if (append) {
          this.items = [...this.items, ...alarms];
        } else {
          this.items = alarms;
        }

        this.meta = result.data.meta;
        this.pagination = result.data.links;
      } catch (e) {
        console.error(e);
      } finally {
        this.isBusy = false;
        this.isFetchingMore = false;
      }
    },
    clickPaginationBtn(direction) {
      if (direction === "prev") {
        this.fetchData(this.pagination.prev);
      } else {
        this.fetchData(this.pagination.next);
      }
    },
    onChangeRecordesPerPage(val) {
      this.defaultFilters.max = Number(val.target.value);

      this.measuredRowHeight = null;
      this.measuredHeaderHeight = null;

      this.items = [];

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      this.fetchData();

      if (this.infiniteScrollEnabled) {
        this.$nextTick(() => this.initIntersectionObserver());
      }
    },
    toggleSearchBar() {
      if (!this.searchBarEnabled) {
        this.searchBarEnabled = true;
      } else {
        this.searchBarEnabled = false;
        this.resetSearchBar();
      }
      this.isCollapseOpen = true;
    },
    resetSearchBar() {
      this.extraFilters.q = null;
      if (this.searchQuery !== "") {
        this.fetchData();
      }
      this.searchQuery = "";
    },
    onTypeSearch: debounce(function (text) {
      this.searchQuery = text;
      this.extraFilters.q = this.searchQuery;
      this.fetchData();
    }, 500),
    updateToolbarsStatus() {
      store.commit(
        "psimConfig/UPDATE_TOOLBARS_COLLAPSED",
        !store.state.psimConfig.isToolbarsCollapsed
      );

      this.isCollapseOpen = true;
    },
    loadMore() {
      if (!this.pagination.next || this.isFetchingMore) return;

      this.isFetchingMore = true;
      this.fetchData(this.pagination.next, { append: true });
    },
    initIntersectionObserver() {
      if (this.observer || !this.$refs.scrollSentinel) return;

      this.observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          this.infiniteScrollEnabled &&
          !this.isFetchingMore &&
          this.pagination.next
        ) {
          this.loadMore();
        }
      },
      {
        root: this.$refs.scrollRoot,
        rootMargin: "40px",
        threshold: 0,
      }
    );

      this.observer.observe(this.$refs.scrollSentinel);
    },
    onUserScroll() {
    if (!this.hasUserScrolled) {
      this.hasUserScrolled = true;
      }
    },
    onRowMetrics({ rowHeight, headerHeight }) {
      this.measuredRowHeight = rowHeight;
      this.measuredHeaderHeight = headerHeight;
    },
  },
  beforeDestroy() {
    this.refreshDataDebounced?.cancel();
    this.source.cancel();

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    EventBus.off("update-alarms", this.onAlarmsUpdated);
    EventBus.off("alarm-recurrence-updated", this.onAlarmRecurrenceUpdated);
    EventBus.off("alarm-device-state-updated", this.onAlarmDeviceStateUpdated);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  },
};
</script>

<style lang="scss" scoped>

.alarms-widget .table.table-sm td {
  padding: 0rem !important;
  max-height: 70px;
}

.pagination-count {
  padding-inline: 1rem;
}

.icon-hover-grow {
  margin-inline: auto;
}

.icon-hover-grow:hover {
  transform: scale(1.25);
}

.alarms-scroll-container {
  position: relative;
  overflow-y: hidden;
  scrollbar-gutter: stable;
}

.infinite-active {
  overflow-y: auto;
}

.table-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 28px;
  padding: 6px 0;

  text-align: center;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;

  z-index: 10;
  pointer-events: none;
}

.checkbox-alarms{
  top: 5px;

  ::v-deep .custom-control-label {
    padding-left: 0 !important;
  }
}

.user-alarms-height {
  max-height: 82px;
}
</style>
